import {
    IIssueHardwareHealthResponse,
    IIssueMachineHealthResponse,
    IssueHardwareHealthResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { NoValue } from 'components/no_value/NoValue';
import { KCFOnlyLozenge } from 'pages/Desk/issues/issue_card/KCFOnlyLozenge';
import { FormattedText } from 'pages/Desk/issues/issue_details/feed/styled';
import { memo, ReactNode, useRef } from 'react';
import { HoverTooltip } from 'shared/hover_tooltip/HoverTooltip';
import styled, { css } from 'styled-components';

const IssueNumberContainer = styled.div<{ $styles?: ReturnType<typeof css> }>`
    display: flex;
    gap: 0.8rem;
    align-items: center;
    ${(props) => props.$styles}
`;

const IssueNumber = styled.div<{ $styles?: ReturnType<typeof css> }>`
    display: inline-block;
    color: var(--text-link);
    &:hover {
        color: var(--text-link-hover);
    }
    ${(props) => props.$styles}
`;

const PopupContent = styled.div<{ $styles?: ReturnType<typeof css> }>`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 40rem;
    max-height: 30rem;
    ${(props) => props.$styles}
`;

export const PreviewPopupTitle = styled.h4<{ $styles?: ReturnType<typeof css> }>`
    margin: 0;
    padding: 0;
    ${(props) => props.$styles}
`;

export const PreviewPopupSection = styled.div<{ $styles?: ReturnType<typeof css> }>`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    ${(props) => props.$styles}
`;

type IssuePreviewPopupProps = Readonly<{
    issue: IIssueHardwareHealthResponse | IIssueMachineHealthResponse;
    containerStyles?: ReturnType<typeof css>;
    issueNumberStyles?: ReturnType<typeof css>;
    popupStyles?: ReturnType<typeof css>;
    children: ReactNode;
}>;

function IssuePreviewPopupBase({
    issue,
    containerStyles,
    issueNumberStyles,
    popupStyles,
    children,
}: IssuePreviewPopupProps) {
    const tooltipAnchorRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <IssueNumberContainer $styles={containerStyles}>
                <IssueNumber ref={tooltipAnchorRef} $styles={issueNumberStyles}>
                    #{issue.IssueNumber}
                </IssueNumber>
                {issue instanceof IssueHardwareHealthResponse && issue.IsInternal && (
                    <KCFOnlyLozenge />
                )}
            </IssueNumberContainer>
            <HoverTooltip anchorRef={tooltipAnchorRef} hideDelayMs={0}>
                <PopupContent $styles={popupStyles}>{children}</PopupContent>
            </HoverTooltip>
        </>
    );
}

export const IssuePreviewPopup = memo(IssuePreviewPopupBase);

type IssuePreviewDescriptionAndRecommendationProps = Readonly<{
    issue: IIssueHardwareHealthResponse | IIssueMachineHealthResponse;
    titleStyles?: ReturnType<typeof css>;
}>;

function IssuePreviewDescriptionAndRecommendationBase({
    issue,
    titleStyles,
}: IssuePreviewDescriptionAndRecommendationProps) {
    return (
        <>
            <PreviewPopupSection>
                <PreviewPopupTitle $styles={titleStyles}>Description</PreviewPopupTitle>
                <FormattedText className="ellipsis-3-lines">
                    {issue.Description || <NoValue />}
                </FormattedText>
            </PreviewPopupSection>
            <PreviewPopupSection>
                <PreviewPopupTitle $styles={titleStyles}>
                    Recommendation
                </PreviewPopupTitle>
                <FormattedText className="ellipsis-3-lines">
                    {issue.RecommendedAction || <NoValue />}
                </FormattedText>
            </PreviewPopupSection>
        </>
    );
}

export const IssuePreviewDescriptionAndRecommendation = memo(
    IssuePreviewDescriptionAndRecommendationBase
);

type IssuePreviewClosingNotesProps = Readonly<{
    issue: IIssueHardwareHealthResponse | IIssueMachineHealthResponse;
    titleStyles?: ReturnType<typeof css>;
}>;

function IssuePreviewClosingNotesBase({
    issue,
    titleStyles,
}: IssuePreviewClosingNotesProps) {
    return (
        <PreviewPopupSection>
            <PreviewPopupTitle $styles={titleStyles}>Resolution Notes</PreviewPopupTitle>
            <FormattedText className="ellipsis-3-lines">
                {issue.ClosingNotes || <NoValue />}
            </FormattedText>
        </PreviewPopupSection>
    );
}

export const IssuePreviewClosingNotes = memo(IssuePreviewClosingNotesBase);
